/**
 * The error summary („Fehlerübersicht“, spec I-2813 §4): the refused writes of
 * one context — a section, a dialog, a grid outside any section — that the
 * kit's `NxErrorSummary` lists. Client-side state only; nothing is stored.
 *
 * Every entry belongs to an origin, `<scope>/<kind>[:<id>]` (K15): the scope
 * is the reporter instance (a grid's instance id, `dialog`, the record a mask
 * shows), so two reporters never replace or clear each other's entries. Every
 * answer carries the attempt token of the write it answers (K14): the summary
 * keeps the newest attempt per origin and ignores answers of older ones. An
 * entry leaves when its origin succeeds or starts again (K9) — except a
 * `saved_then_failed` entry, which only its own „Neu laden“ removes (K18) and
 * which is never stale: the write may have landed whichever attempt overtook
 * it, so a late one still shows and locks.
 *
 * An owner calls `useErrorSummary()` (creates, provides, clears with its
 * scope) and renders `NxErrorSummary` with `errorSummaryBindings()`; reporters
 * below it call `injectErrorSummary()` and report through
 * `reportWriteFailure` (`./writeFailure`).
 */
import {
  computed,
  hasInjectionContext,
  inject,
  onScopeDispose,
  provide,
  ref,
  shallowRef,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from 'vue';
// Type only, like the rest of `@blpde/connect/http` (see problemHelpers.ts).
import type { NxErrorSummaryEntry } from '@blpde/nx-ui';
import type { ShowToast, Translate } from './problemHelpers';

/**
 * The originator of an entry: `<scope>/<kind>[:<id>]` (K15), built by
 * {@link originKey}. The scope contains no `/` — the part before the first
 * `/` IS the scope (`drop`) — and neither scope nor id contains `|`, which
 * separates the entries `reportWriteFailure` derives from an origin
 * (`<origin>|<field>`). `originKey` throws on either.
 */
export type OriginKey = `${string}/${string}`;

/** What reported: a field, a grid row's cells, a grid's create row or an action. */
export type OriginKind = 'field' | 'row' | 'create-row' | 'action';

/** The token of one write attempt of an origin; a newer attempt has a higher token. */
export type Attempt = number;

/** One refused write in the summary. */
export interface ErrorSummaryEntry {
  origin: OriginKey;
  /**
   * The attempt the entry answers (K14): from `ErrorSummary.begin(origin)`, or
   * a token the reporter keeps itself (the grid's `cellSaveStatus` tokens).
   * One origin never mixes the two.
   */
  attempt: Attempt;
  /** What the entry belongs to — a subsection, a grid row and column, a field label. */
  area?: string;
  /** The title of a lone entry (`problem.title`, the possibly-saved title). */
  title?: string;
  /** The sentence the backend sent (`problem.detail`). */
  detail: string;
  /** The request id („Fehler-ID“) of the refused request. */
  requestId?: string;
  /**
   * The write may have been saved (`saved_then_failed`, K18): the entry is
   * sticky — neither a restart nor a success of its origin removes it, nor a
   * later ordinary refusal of the origin (K31), only its own `reload` — and
   * the summary is `locked` while it exists. It is never stale either: an
   * attempt a newer one overtook may still have landed, so its answer shows
   * (K14 yields to K18); only a dropped scope ignores it.
   */
  savedThenFailed?: boolean;
  /** „Neu laden“: what reloading means for this origin (refetch the grid, close the dialog). */
  reload?: () => void;
}

/**
 * What `ErrorSummary.report` did with an entry: `shown` — it is in the summary;
 * `kept` — the origin's sticky entry stays and the new one is not shown (K31);
 * `overtaken` — a `saved_then_failed` answer of an attempt older than the
 * origin's newest: it is in the summary and locks it, but the reporter's
 * inputs belong to the newer attempt, so nothing else of the answer shows
 * (K18 over K14, K37); `ignored` — the answer is stale or its scope was
 * dropped (K14), nothing shows.
 */
export type ReportOutcome = 'shown' | 'kept' | 'overtaken' | 'ignored';

/** The refused writes of one context (spec I-2813 §4, §8). */
export interface ErrorSummary {
  /** The entries in the order they first came. */
  entries: Readonly<Ref<readonly ErrorSummaryEntry[]>>;
  /**
   * Starts an attempt of origin: the newest token; removes the origin's
   * entries but a sticky one (K9). On a dropped scope the token is as dead as
   * the scope: its answers stay ignored.
   */
  begin(origin: OriginKey): Attempt;
  /**
   * Shows an entry; a newer refusal of the same origin replaces the origin's
   * entries (K10) — except a sticky `saved_then_failed` entry, which an
   * ordinary refusal of its origin never replaces: the entry and the lock stay
   * and the answer is `kept` (K31). `ignored` when the origin's scope was
   * dropped, or when entry.attempt is older than the origin's newest (K14) —
   * unless the entry is `savedThenFailed`: that write may have landed whichever
   * attempt overtook it, so it shows and locks regardless of its age (K18) and
   * the answer is `overtaken`.
   */
  report(entry: ErrorSummaryEntry): ReportOutcome;
  /**
   * The origin succeeded (or starts again): removes its entries but a sticky
   * one. Ignored when attempt is older than the origin's newest; without an
   * attempt, the newest is meant.
   */
  resolve(origin: OriginKey, attempt?: Attempt): void;
  /** A `saved_then_failed` entry exists: the owner blocks submits and automatic retries (K18). */
  locked: Readonly<Ref<boolean>>;
  /** The entry's own „Neu laden“: runs its `reload`, then removes the origin's entries, sticky included. */
  reload(origin: OriginKey): void;
  /**
   * The reporter of `scope` is gone (a grid unmounted inside a living
   * section, a mask that shows another record): removes its entries, sticky
   * included, and ignores every later answer of that scope — a sticky one
   * too — so a late refusal cannot bring an entry back that nobody would
   * remove (Review Focus 1). Final: a scope is an instance, a reporter that
   * mounts again (or shows the next record) takes a new one. Neither `begin`
   * nor an attempt-less `reportWriteFailure` opens a dropped scope again, and
   * the attempt bookkeeping stays, so a pre-drop answer of any origin of the
   * scope never shows later.
   */
  drop(scope: string): void;
  /** A deliberate submit failed: the owner moves the focus to the summary when it is out of view (rule 10). */
  requestFocus(): void;
  /** Counts `requestFocus` calls; the owner watches it and calls the kit summary's `focus()`. */
  focusRequests: Readonly<Ref<number>>;
  /**
   * Removes every entry, sticky ones included — the owner closes or resets,
   * so an owner that is `locked` must not call it before the sticky entry's
   * „Neu laden“ did. The attempt bookkeeping stays: an answer of an attempt
   * older than an origin's newest stays ignored after the clear.
   */
  clear(): void;
}

/**
 * The origin key of a reporter: `<scope>/<kind>` or `<scope>/<kind>:<id>`
 * (K15). Throws on a scope with `/` (it would be cut at the slash and `drop`
 * would miss it) and on a `|` in scope or id (reserved for derived entries).
 */
export function originKey(scope: string, kind: OriginKind, id?: string): OriginKey {
  if (scope.includes('/') || scope.includes('|')) {
    throw new Error(`originKey: scope "${scope}" must not contain "/" or "|"`);
  }
  if (id?.includes('|')) throw new Error(`originKey: id "${id}" must not contain "|"`);
  return id === undefined ? `${scope}/${kind}` : `${scope}/${kind}:${id}`;
}

/** The origin an entry key belongs to: the key itself, or the part before `|` of a derived entry. */
function baseOrigin(key: OriginKey): OriginKey {
  const bar = key.indexOf('|');
  return bar < 0 ? key : (key.slice(0, bar) as OriginKey);
}

/** The scope of an origin: the part before its first `/`. */
function scopeOf(origin: OriginKey): string {
  return origin.slice(0, origin.indexOf('/'));
}

/** Whether key is origin's own entry or one derived from it. */
function belongsTo(key: string, origin: OriginKey): boolean {
  return key === origin || key.startsWith(`${origin}|`);
}

/**
 * A summary without a component around it — for tests and for owners that
 * are no setup (the decider's own tests). `useErrorSummary` is the owner's way.
 */
export function createErrorSummary(): ErrorSummary {
  const store = shallowRef<ReadonlyMap<string, ErrorSummaryEntry>>(new Map());
  const newest = new Map<OriginKey, Attempt>();
  const dropped = new Set<string>();
  const focusCount = ref(0);
  let counter = 0;

  const entries = computed(() => [...store.value.values()]);
  const locked = computed(() => entries.value.some((entry) => entry.savedThenFailed === true));

  const isDropped = (origin: OriginKey) => dropped.has(scopeOf(origin));
  const isStale = (origin: OriginKey, attempt: Attempt) =>
    attempt < (newest.get(origin) ?? -Infinity);
  const isClosed = (origin: OriginKey, attempt: Attempt) =>
    isDropped(origin) || isStale(origin, attempt);

  function advance(origin: OriginKey, attempt: Attempt): void {
    newest.set(origin, Math.max(attempt, newest.get(origin) ?? attempt));
  }

  function remove(matches: (key: string, entry: ErrorSummaryEntry) => boolean): void {
    let next: Map<string, ErrorSummaryEntry> | undefined;
    for (const [key, entry] of store.value) {
      if (!matches(key, entry)) continue;
      next ??= new Map(store.value);
      next.delete(key);
    }
    if (next) store.value = next;
  }

  return {
    entries,
    locked,
    focusRequests: computed(() => focusCount.value),

    begin(origin) {
      counter = Math.max(counter, newest.get(origin) ?? 0) + 1;
      newest.set(origin, counter);
      remove((key, entry) => belongsTo(key, origin) && !entry.savedThenFailed);
      return counter;
    },

    report(entry) {
      const origin = baseOrigin(entry.origin);
      if (isDropped(origin)) return 'ignored';
      // A stale ordinary answer shows nothing (K14); a stale sticky one still
      // may have landed and must show and lock (K18) — the attempt that
      // overtook it stays the newest.
      const stale = isStale(origin, entry.attempt);
      if (stale && !entry.savedThenFailed) return 'ignored';
      advance(origin, entry.attempt);
      // A sticky entry outlives a later ordinary refusal of its origin (K31):
      // the attempt counts (an older answer stays stale), the entry does not.
      const current = store.value.get(entry.origin);
      if (current?.savedThenFailed && !entry.savedThenFailed) return 'kept';
      const next = new Map(store.value);
      // A new refusal replaces what an older one of the origin derived.
      if (entry.origin === origin) {
        for (const [key, old] of next) {
          if (key !== origin && belongsTo(key, origin) && old.attempt !== entry.attempt) {
            next.delete(key);
          }
        }
      }
      next.set(entry.origin, entry);
      store.value = next;
      return stale ? 'overtaken' : 'shown';
    },

    resolve(origin, attempt = newest.get(origin) ?? 0) {
      if (isClosed(origin, attempt)) return;
      advance(origin, attempt);
      remove((key, entry) => belongsTo(key, origin) && !entry.savedThenFailed);
    },

    reload(origin) {
      const entry = store.value.get(origin);
      try {
        entry?.reload?.();
      } finally {
        remove((key) => belongsTo(key, origin));
      }
    },

    drop(scope) {
      dropped.add(scope);
      const prefix = `${scope}/`;
      remove((key) => key.startsWith(prefix));
      // `newest` stays: the scope is closed for good, and should it ever be
      // read again, an answer issued before the drop is still not newer.
    },

    requestFocus() {
      focusCount.value += 1;
    },

    clear() {
      if (store.value.size > 0) store.value = new Map();
    },
  };
}

/** The injection key of the summary a reporter reports into; `null` cuts the chain (K3). */
const errorSummaryKey: InjectionKey<ErrorSummary | null> = Symbol('errorSummary');

/**
 * The owner's summary: created, provided to every descendant and cleared when
 * the owner's scope ends (the section unmounts — K9). Call it in setup.
 */
export function useErrorSummary(): ErrorSummary {
  const summary = createErrorSummary();
  provide(errorSummaryKey, summary);
  onScopeDispose(() => summary.clear());
  return summary;
}

/**
 * The summary a reporter reports into: the nearest owner's; `null` below
 * {@link provideNoErrorSummary} (a sidebar section — report by toast);
 * `undefined` without any owner, or outside a setup.
 */
export function injectErrorSummary(): ErrorSummary | null | undefined {
  return hasInjectionContext() ? inject(errorSummaryKey, undefined) : undefined;
}

/**
 * Cuts the chain: descendants see no summary (`null`) even below an owner —
 * a sidebar section, whose actions answer with a toast (K3). Call it in setup.
 */
export function provideNoErrorSummary(): void {
  provide(errorSummaryKey, null);
}

/**
 * What an owner hands `NxErrorSummary`: the entries in the kit's shape (id =
 * origin, `reload` offered when the entry has a reload — K19) and the answers
 * to its two emits — `reload(id)` runs the entry's reload, `copy-failed(id)`
 * shows the request id in a persistent error toast, since the summary shows
 * it nowhere (K21).
 */
export function errorSummaryBindings(
  summary: ErrorSummary,
  host: { showToast: ShowToast; t: Translate }
): {
  entries: ComputedRef<NxErrorSummaryEntry[]>;
  onReload(id: string): void;
  onCopyFailed(requestId: string): void;
} {
  return {
    entries: computed(() =>
      summary.entries.value.map((entry) => ({
        id: entry.origin,
        area: entry.area,
        title: entry.title,
        detail: entry.detail,
        requestId: entry.requestId,
        reload: entry.reload !== undefined,
      }))
    ),
    onReload(id) {
      summary.reload(id as OriginKey);
    },
    onCopyFailed(requestId) {
      host.showToast({
        message: host.t('connect.writeFailure.copyFailed', { requestId }),
        kind: 'error',
        persistent: true,
      });
    },
  };
}
